import React from 'react';
import './DraftConfig.css';

const POSITION_HINTS = {
  FLEX: 'WR/RB',
  SFLEX: 'QB/WR/RB'
};

const DraftConfig = ({
  config,
  onConfigChange,
  onStartDraft,
  onViewChange,
  customPlayerData,
  onFileUpload,
  savedPlayerFiles,
  selectedFileName,
  loadingFileName,
  onSelectFile,
  onDeleteFile,
  onRenameFile,
  draftName,
  onDraftNameChange
}) => {
  const handleTeamCountChange = (value) => {
    onConfigChange({
      ...config,
      teamCount: value,
      teamNames: Array(value).fill('')
    });
  };

  const handlePositionChange = (position, e) => {
    const maxValue = position === 'BENCH' ? 10 : 5;
    const value = Math.min(Math.max(0, parseInt(e.target.value)), maxValue);
    onConfigChange({
      ...config,
      positions: {
        ...config.positions,
        [position]: value
      }
    });
  };

  const renderToggleButtons = (position, currentValue) => {
    const max = position === 'BENCH' ? 10 : 5;
    const buttons = [];

    if (position === 'BENCH') {
      // First row: 0-5
      const row1 = (
        <div key="row1" className="toggle-row">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <button
              key={i}
              className={`toggle-button ${currentValue === i ? 'active' : ''}`}
              onClick={() => handlePositionChange(position, { target: { value: i } })}
            >
              {i}
            </button>
          ))}
        </div>
      );

      // Second row: 6-10
      const row2 = (
        <div key="row2" className="toggle-row">
          {[6, 7, 8, 9, 10].map(i => (
            <button
              key={i}
              className={`toggle-button ${currentValue === i ? 'active' : ''}`}
              onClick={() => handlePositionChange(position, { target: { value: i } })}
            >
              {i}
            </button>
          ))}
        </div>
      );

      return [row1, row2];
    }

    // For non-bench positions, keep the original single-row behavior
    for (let i = 0; i <= max; i++) {
      buttons.push(
        <button
          key={i}
          className={`toggle-button ${currentValue === i ? 'active' : ''}`}
          onClick={() => handlePositionChange(position, { target: { value: i } })}
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  const renderTeamCountButtons = () => {
    return Array.from({length: 7}, (_, i) => i + 8).map(num => (
      <button
        key={num}
        className={`toggle-button ${config.teamCount === num ? 'active' : ''}`}
        onClick={() => handleTeamCountChange(num)}
      >
        {num}
      </button>
    ));
  };

  return (
    <div className="config-form">
      <div className="settings-section">
        <div className="form-row">
          <label htmlFor="draftName">Draft Name:</label>
          <input
            type="text"
            id="draftName"
            value={draftName}
            onChange={(e) => onDraftNameChange(e.target.value)}
            className="draft-name-input"
          />
        </div>
        <div className="form-row">
          <label htmlFor="teamCount">Team Count:</label>
          <div className="toggle-group">
            {renderTeamCountButtons()}
          </div>
        </div>
      </div>

      <div className="position-settings">
        <h3>Position Settings:</h3>
        <div className="position-column left">
          {['QB', 'RB', 'WR', 'TE', 'DEF'].map(position => (
            <div className="form-row" key={position} data-position={position}>
              <label htmlFor={position}>{position}:</label>
              <div className="toggle-group">
                {renderToggleButtons(position, config.positions[position])}
              </div>
            </div>
          ))}
        </div>
        <div className="position-column right">
          {['FLEX', 'SFLEX', 'K', 'BENCH'].map(position => (
            <div className="form-row" key={position} data-position={position}>
              <label htmlFor={position}>
                {position}:
                {POSITION_HINTS[position] && <span className="label-hint">{POSITION_HINTS[position]}</span>}
              </label>
              <div className="toggle-group">
                {renderToggleButtons(position, config.positions[position])}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="file-upload-section">
        {savedPlayerFiles.length > 0 && (
          <div className="saved-files-list">
            {savedPlayerFiles.map(file => (
              <div
                key={file.name}
                className={`saved-file-item ${selectedFileName === file.name ? 'selected' : ''}`}
              >
                <button
                  className="saved-file-select"
                  onClick={() => onSelectFile(file.name)}
                  disabled={loadingFileName !== null}
                >
                  {selectedFileName === file.name ? '✓ ' : ''}
                  {file.name}
                  {loadingFileName === file.name && <span className="saved-file-loading"> Loading...</span>}
                </button>
                <span className="saved-file-date">
                  {new Date(file.lastModified).toLocaleDateString()}
                </span>
                <button className="saved-file-action" onClick={() => onRenameFile(file.name)}>
                  Rename
                </button>
                <button className="saved-file-action delete" onClick={() => onDeleteFile(file.name)}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="file-upload-container">
          <input
            type="file"
            accept=".json"
            onChange={onFileUpload}
            id="player-data-upload"
            className="file-upload-input"
          />
          <label htmlFor="player-data-upload" className="file-upload-label">
            {customPlayerData ? 'Upload New Player Data File' : 'Upload Player Data File'}
          </label>
          {customPlayerData && (
            <span className="file-upload-success">✓ Custom data loaded</span>
          )}
        </div>
        <p className="file-upload-help">
          Upload a JSON file with player projections, or select a previously uploaded file above.
          Uploaded files are saved to your account.
        </p>
      </div>
    </div>
  );
};

export default DraftConfig;